import { Box, ButtonBase, Typography } from "@mui/material";
import AISparkleIcon from "../../assets/AISparkleIcon";
import { clamp } from "../../utils/AiReviewHelpers";

function AiLensGlyph({ expanded, T }) {
  if (expanded) {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <ellipse cx="11" cy="11" rx="6.2" ry="2.7" stroke={T.brand} strokeWidth="1.6" />
        <path d="M5.5 8.4c1.8-2.2 3.7-3.3 5.5-3.3s3.7 1.1 5.5 3.3" stroke={T.brandLight} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M5.5 13.6c1.8 2.2 3.7 3.3 5.5 3.3s3.7-1.1 5.5-3.3" stroke={T.brandLight} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="11" cy="11" r="1.4" fill={T.brand} />
      </svg>
    );
  }

  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6.6" stroke={T.brandLight} strokeWidth="1.6" opacity="0.9" />
      <path d="M11 4.4A6.6 6.6 0 0 1 17.6 11" stroke={T.brandLight} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="11" cy="11" r="2.2" fill={T.brandLight} opacity="0.22" />
      <circle cx="14.8" cy="7.2" r="1.25" fill={T.brandLight} />
    </svg>
  );
}

export function AiReviewHeader({ score, expanded, setExpanded, onClose, T }) {
  const scoreDisplay = Number.isFinite(Number(score))
    ? `${clamp(Number(score), 0, 5).toFixed(1)} / 5`
    : "-";

  const headerStyle = T
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 18px 12px",
        background: `linear-gradient(90deg, ${T.brand} 0%, ${T.brandLight} 56%, rgba(255,255,255,0.92) 100%)`,
        borderBottom: `1px solid ${T.borderSubtle}`,
        fontFamily: T.fontFamily,
      }
    : undefined;

  if (!T) {
    return (
      <Box className="ai-review-popup__header">
        <Box className="ai-review-popup__headerLeft">
          <span className="ai-review-popup__sparkle"><AISparkleIcon /></span>
          <Typography className="ai-review-popup__title">AI Review</Typography>
        </Box>
        <Box className="ai-review-popup__headerRight">
          <Box className="ai-review-popup__scoreBadge">
            <span className="ai-review-popup__scoreKicker">AI score</span>
            <span className="ai-review-popup__scoreValue">{scoreDisplay}</span>
          </Box>
          <ButtonBase
            className="ai-review-popup__headerClose"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            component="button"
            type="button"
            aria-label="Close AI review panel"
          >
            <span className="ai-review-popup__headerCloseInner">x</span>
          </ButtonBase>
        </Box>
      </Box>
    );
  }

  return (
    <div style={{ ...headerStyle, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.62)",
            border: `1px solid ${T.brandLight}22`,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(0,0,0,0.18)",
          }}
        >
          <span style={{ display: "flex", opacity: 0.95 }}><AISparkleIcon sx={{ fontSize: 20 }} /></span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              color: T.textPrimary,
              textTransform: "uppercase",
              fontFamily: T.fontFamily,
            }}
          >
            AI Review
          </span>
        </div>
      </div>

      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          setExpanded?.(!expanded);
        }}
        component="button"
        type="button"
        aria-label={expanded ? "Collapse deep scan" : "Open deep scan"}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: expanded
            ? "radial-gradient(circle at 50% 40%, rgba(255,255,255,0.96) 0%, rgba(224,241,255,0.94) 58%, rgba(202,228,250,0.9) 100%)"
            : "radial-gradient(circle at 50% 40%, rgba(16,34,68,0.96) 0%, rgba(8,18,36,0.98) 68%, rgba(5,10,22,1) 100%)",
          border: expanded
            ? `1.5px solid ${T.brandLight}44`
            : `1.5px solid rgba(255,255,255,0.08)`,
          boxShadow: expanded
            ? "0 12px 22px rgba(0,0,0,0.16), inset 0 2px 0 rgba(255,255,255,0.72)"
            : `0 16px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 18px ${T.brandLight}18`,
          transition: "all 0.22s ease",
          zIndex: 2,
          "&:hover": {
            transform: "translate(-50%, -50%) scale(1.05)",
          },
        }}
      >
        <span
          style={{
            position: "absolute",
            inset: -14,
            borderRadius: "50%",
            border: expanded ? "none" : `3px solid ${T.brandLight}`,
            boxShadow: expanded ? "none" : `0 0 22px ${T.brandLight}66`,
            pointerEvents: "none",
            animation: expanded ? "none" : "airp-lens-pulse 1.55s ease-out infinite",
          }}
        />
        <span
          style={{
            position: "absolute",
            inset: -6,
            borderRadius: "50%",
            border: expanded ? "none" : `2px solid ${T.brandLight}A8`,
            boxShadow: expanded ? "none" : `0 0 20px ${T.brandLight}44`,
            pointerEvents: "none",
            animation: expanded ? "none" : "airp-lens-ring-out 1.3s ease-out infinite",
          }}
        />
        <span
          style={{
            position: "absolute",
            inset: 5,
            borderRadius: "50%",
            border: expanded ? `1px solid ${T.brandLight}24` : `1px solid ${T.brandLight}16`,
            boxShadow: expanded ? "inset 0 1px 0 rgba(255,255,255,0.45)" : "inset 0 1px 0 rgba(255,255,255,0.04)",
            pointerEvents: "none",
          }}
        />
        <span
          style={{
            position: "absolute",
            inset: 8,
            borderRadius: "50%",
            background: expanded ? "transparent" : `conic-gradient(from 0deg, transparent 0deg 290deg, ${T.brandLight}90 322deg 344deg, transparent 360deg)`,
            mixBlendMode: "screen",
            pointerEvents: "none",
            animation: expanded ? "none" : "airp-lens-sweep 2.6s linear infinite",
          }}
        />
        <AiLensGlyph expanded={expanded} T={T} />
      </ButtonBase>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 44, marginLeft: "auto" }}>
        <div
          style={{
            padding: "5px 14px",
            borderRadius: 14,
            background: `linear-gradient(180deg, ${T.surface2} 0%, ${T.panelBg} 100%)`,
            border: `1px solid ${T.brandLight}25`,
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06)`,
          }}
        >
          <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.14em", color: T.textMuted, fontFamily: T.fontFamily, textTransform: "uppercase" }}>AI score</span>
          <span
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: T.brandLight,
              letterSpacing: "0.02em",
              fontFamily: T.fontFamily,
              textShadow: `0 0 10px ${T.brandLight}20`,
            }}
          >
            {scoreDisplay}
          </span>
        </div>
      </div>
    </div>
  );
}
